import * as React from 'react';
import { Check, Eye, Shield, Users, X } from 'lucide-react';

import { classNames } from '@/utils/classNames.utils';
import { MIN_LEN } from '@/constants';
import { useStepManageAccessReview } from '@/hooks/manageAccess/useStepManageAccessReview';

type ReviewPersonRole = 'Owner' | 'Member';

export interface StepManageAccessReviewProps {
  onClose: () => void;
  className?: string;
}

function renderRoleBadge(role: ReviewPersonRole): React.JSX.Element {
  const roleIcon = role === 'Owner' ? <Shield className="h-3 w-3" /> : <Eye className="h-3 w-3" />;

  return (
    <div className="rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 flex items-center space-x-1">
      {roleIcon}
      <span>{role}</span>
    </div>
  );
}

function StepManageAccessReview(props: StepManageAccessReviewProps): React.JSX.Element {
  const { state: reviewState, handlers } = useStepManageAccessReview();
  const { groups, totalPeopleCount } = reviewState;

  return (
    <div className={classNames('space-y-6', props.className)}>
      <div>
        <h3 className="text-lg font-semibold mb-2">Review &amp; Confirm</h3>
        <p className="text-muted-foreground">Review access changes before applying them.</p>
      </div>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="flex flex-col space-y-1.5 p-6 pb-3">
          <h3 className="font-semibold tracking-tight flex items-center space-x-2 text-base">
            <Users className="h-5 w-5" />
            <span>People ({totalPeopleCount})</span>
          </h3>
        </div>

        <div className="p-6 pt-0 space-y-6">
          {groups.length === MIN_LEN ? (
            <div className="text-sm text-muted-foreground">No changes selected.</div>
          ) : (
            groups.map((group) => (
              <div key={group.title} className="space-y-3">
                <div className="flex items-center space-x-2 text-sm font-medium text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>
                    {group.title} ({group.people.length})
                  </span>
                </div>

                <div className="space-y-2">
                  {group.people.map((person) => (
                    <div key={person.identifier} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-primary">{person.initials}</span>
                        </div>

                        <div>
                          <div className="font-medium text-sm">{person.name}</div>
                          <div className="text-xs text-muted-foreground">{person.email}</div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {renderRoleBadge(person.role)}

                        {person.isRemovable && (
                          <button
                            type="button"
                            aria-label={`Remove ${person.name}`}
                            onClick={() => {
                              handlers.handleRemovePerson(person.identifier);
                            }}
                            className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="rounded-lg border text-card-foreground shadow-sm border-primary/20 bg-primary/5">
        <div className="p-4">
          <div className="flex items-start space-x-3">
            <Check className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <div className="font-medium text-primary">Ready to Apply</div>
              <div className="text-sm text-muted-foreground mt-1">
                Access changes will be applied after you confirm. Invitations may be sent to newly added external people.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

StepManageAccessReview.displayName = 'StepManageAccessReview';

export default StepManageAccessReview;
